import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { Language, detectLanguage, saveLanguagePreference, getTranslations, GlobalTranslations } from '../services/localization'

interface LocalizationContextType {
  language: Language
  translations: GlobalTranslations
  changeLanguage: (lang: Language) => void
  t: (keyPath: string) => string
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(undefined)

interface LocalizationProviderProps {
  children: ReactNode
}

export function LocalizationProvider({ children }: LocalizationProviderProps) {
  const [language, setLanguage] = useState<Language>(() => detectLanguage())
  const [translations, setTranslations] = useState<GlobalTranslations>(() => getTranslations(detectLanguage()))

  // Update translations when language changes
  useEffect(() => {
    const newTranslations = getTranslations(language)
    setTranslations(newTranslations)
    // Update HTML lang attribute for SEO
    document.documentElement.lang = language
  }, [language])

  // Change language handler
  const changeLanguage = useCallback((lang: Language) => {
    setLanguage(lang)
    saveLanguagePreference(lang)
  }, [])

  // Translation function
  const t = useCallback((keyPath: string): string => {
    const keys = keyPath.split('.')
    let value: any = translations
    
    for (const key of keys) {
      if (value && typeof value === 'object' && key in value) {
        value = value[key]
      } else {
        return keyPath // Return key path if translation not found
      }
    }
    
    return typeof value === 'string' ? value : keyPath
  }, [translations])

  return (
    <LocalizationContext.Provider value={{ language, translations, changeLanguage, t }}>
      {children}
    </LocalizationContext.Provider>
  )
}

export function useLocalizationContext() {
  const context = useContext(LocalizationContext)
  if (context === undefined) {
    throw new Error('useLocalizationContext must be used within a LocalizationProvider')
  }
  return context
}
